import { useEffect, useRef, useState } from 'react'
import { api } from '../api/client'
import type {
  Assessment, MarketEvent, NewsItem, ResearchCase, TradeIdea, TradeIdeaDetail,
} from '../api/client'
import { subscribe } from '../api/events'

/**
 * Wie viele Einträge der Feed hält.
 *
 * Auf einem Bildschirm, der tagelang läuft, wüchse die Liste sonst unbegrenzt
 * — irgendwann ist der Speicher voll und das Rendern zäh. Wer weiter zurück
 * will, geht in die jeweilige Liste.
 */
const MAX_ENTRIES = 200

/** Wie viele je Quelle beim Öffnen geladen werden. */
const INITIAL_PER_SOURCE = 25

/** Die fünf Kanäle des Feeds — dieselben Namen wie im Ereignisstrom. */
export type FeedTopic = 'news' | 'research' | 'event' | 'assessment'
                      | 'trade_idea'

/** Ein Eintrag im Feed. */
export interface FeedEntry {
  /** Eindeutig je Eintrag — auch wenn derselbe Datensatz zweimal kommt. */
  key: string
  /**
   * Wann es ERSCHIEN, nicht wann der Hinweis eintraf.
   *
   * Bei einer Meldung das Erscheinungsdatum, bei einem Fall die letzte
   * wesentliche Entwicklung, bei einer Einschätzung ihr Stand, bei einer
   * Idee die Anlage oder die jüngste Fortschreibung. Ein Termin hat nur
   * einen Tag und keine Uhrzeit — er steht auf Mitternacht und wird von der
   * Seite als eigener Block gezeigt, nicht in der Zeitreihe.
   *
   * Nur wo es keinen Datensatz gibt (ein Hinweis ohne Kennung), bleibt der
   * Eintreffzeitpunkt: Sonst hätte der Eintrag gar keinen und fiele aus der
   * Sortierung.
   */
  at: Date
  topic: string
  id: number | null
  news?: NewsItem
  case?: ResearchCase
  event?: MarketEvent
  assessment?: Assessment
  idea?: TradeIdeaDetail
  /** Warum nichts nachgeladen werden konnte. */
  note?: string
  /** Seit dem letzten Rendern dazugekommen — für die kurze Hervorhebung. */
  fresh?: boolean
}

// ------------------------------------------------------------- „heute"

/** Der lokale Kalendertag als `YYYY-MM-DD`. */
function lokalerTag(d: Date): string {
  const zwei = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${zwei(d.getMonth() + 1)}-${zwei(d.getDate())}`
}

function heuteBeginn(): Date {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function heuteEnde(): Date {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)
}

function istHeute(iso: string | null | undefined): boolean {
  return Boolean(iso) && lokalerTag(new Date(iso!)) === lokalerTag(new Date())
}

/**
 * Ob ein Termin heute stattfindet.
 *
 * Mit seiner Genauigkeit: Ein Monatstermin „September" zählt an jedem Tag
 * des Monats — bei dieser Genauigkeit ist das die ehrliche Antwort. Ohne
 * Datum gibt es keinen Tag, an dem er stattfände.
 */
function terminHeute(e: MarketEvent): boolean {
  if (!e.date_from) return false
  const heute = lokalerTag(new Date())
  return e.date_from <= heute && (e.date_to ?? e.date_from) >= heute
}

/**
 * Wann eine Idee im Feed steht: bei der jüngsten Fortschreibung, wenn die
 * von heute ist — sonst bei der Anlage. Der Verlauf steht neueste zuerst.
 */
function ideeZeit(idee: TradeIdeaDetail): Date {
  const juengste = idee.updates[0]
  if (juengste && istHeute(juengste.recorded_at)) {
    return new Date(juengste.recorded_at)
  }
  return new Date(idee.created)
}

/**
 * Eine Idee aus der Liste als Detail ohne Verlauf.
 *
 * Für eine heute ANGELEGTE Idee sagt der Verlauf nichts, was die Liste nicht
 * schon sagt — die Zeile heißt „Neue Idee". Das Detail braucht nur, wer
 * heute fortgeschrieben wurde, und das sind wenige.
 */
function ohneVerlauf(idee: TradeIdea): TradeIdeaDetail {
  return { ...idee, tranches: [], targets: [], updates: [], transactions: [],
           open_orders: [], sources: [] }
}

/** Was `at` bei einem Datensatz ist. */
function zeitpunkt(teil: Partial<FeedEntry>): Date | null {
  if (teil.news) return new Date(teil.news.published_at)
  if (teil.case) {
    // Ohne wesentliche Entwicklung gilt der Zeitpunkt der letzten Änderung —
    // sonst hätte ein frisch angelegter Fall keinen Platz in der Zeitachse.
    const fall = teil.case
    const wert = fall.last_material_update_at ?? fall.updated ?? fall.opened_at
    return wert ? new Date(wert) : null
  }
  if (teil.event) {
    // HEUTE, nicht der Beginn des Termins: Ein Monatstermin „September"
    // beginnt am Ersten, findet aber auch heute statt — und heute wird er
    // gezeigt. Auf seinen Beginn einsortiert stuende er unter dem 1. des
    // Monats mit einem eigenen Block „Termine heute", der dort keiner ist.
    // Mitternacht, weil ein Termin keine Uhrzeit hat.
    return heuteBeginn()
  }
  if (teil.assessment) return new Date(teil.assessment.assessed_at)
  if (teil.idea) return ideeZeit(teil.idea)
  return null
}

/** Nach Zeit absteigend, Doppelte über (topic, id) verworfen, begrenzt. */
function zusammenfuehren(bisher: FeedEntry[],
                         neue: FeedEntry[]): FeedEntry[] {
  const gesehen = new Set<string>()
  const alles = [...neue, ...bisher]
  const eindeutig: FeedEntry[] = []

  for (const eintrag of alles) {
    // Ein Eintrag ohne Kennung ist nicht mit anderen zu vergleichen — er
    // bleibt immer. Für die übrigen zählt (topic, id): Trifft während des
    // Ladens ein Hinweis zu einer Meldung ein, die auch in der Liste steht,
    // stünde sie sonst zweimal. Bei einer Idee heißt das auch: Die zweite
    // Fortschreibung am selben Tag ERSETZT die Zeile, statt sie zu doppeln.
    const schluessel = eintrag.id === null ? null
      : `${eintrag.topic}-${eintrag.id}`
    if (schluessel !== null) {
      if (gesehen.has(schluessel)) continue
      gesehen.add(schluessel)
    }
    eindeutig.push(eintrag)
  }

  return eindeutig
    .sort((a, b) => b.at.getTime() - a.at.getTime())
    .slice(0, MAX_ENTRIES)
}

/**
 * Der Bestand beim Öffnen, danach was dazukommt.
 *
 * Beim Öffnen werden die vorhandenen Listen einmal geladen — über dieselben
 * Endpunkte, die auch die Listenansichten benutzen. Meldungen und Fälle: die
 * jüngsten. Termine, Einschätzungen, Ideen: NUR HEUTE. Das ist der Zuschnitt
 * des Feeds — was heute ansteht, was heute beurteilt wurde, was heute an
 * Ideen entstand oder sich bewegte.
 *
 * Danach hält der Ereignisstrom ihn aktuell. Ein Hinweis trägt nur
 * `{topic, id}`, also wird der Datensatz nachgeladen — und der Zeitstempel
 * kommt aus ihm, nicht aus der Uhr. Ein nachgeladener Termin, der nicht
 * heute ist, oder eine Einschätzung mit altem Stand fällt dabei wieder
 * heraus: Der Strom meldet alles Neue, die Auswahl trifft der Feed.
 */
export function useEventFeed(topics: readonly string[],
                             enabled: boolean = true
                             ): { entries: FeedEntry[]; loading: boolean } {
  const [entries, setEntries] = useState<FeedEntry[]>([])
  const [loading, setLoading] = useState(enabled)
  // Ein Zähler für den Schlüssel: Zeitstempel allein reichen nicht, zwei
  // Einträge können denselben tragen.
  const counter = useRef(0)

  const key = topics.join(',')

  useEffect(() => {
    if (!enabled) {
      setLoading(false)
      return
    }
    const controller = new AbortController()
    const signal = controller.signal
    const schluessel = (topic: string) => `${topic}-${counter.current++}`
    const eintrag = (topic: string, id: number,
                     teil: Partial<FeedEntry>): FeedEntry | null => {
      const at = zeitpunkt(teil)
      return at ? { key: schluessel(topic), at, topic, id, ...teil } : null
    }

    // ------------------------------------------------------- der Bestand
    //
    // JEDE QUELLE FÜR SICH. Fällt eine aus — ein 429 der Ratenbegrenzung,
    // ein fehlendes Recht —, stehen die anderen trotzdem da. Vorher lud
    // alles in einem Zug, und ein Fehler bei der letzten Quelle warf den
    // ganzen Bestand weg: Der Feed blieb leer, obwohl vier Fünftel da waren.
    const sammle = (liste: (FeedEntry | null)[]) =>
      setEntries((bisher) => zusammenfuehren(
        bisher, liste.filter((e): e is FeedEntry => e !== null)))

    const quellen: Promise<void>[] = []

    if (topics.includes('news')) {
      quellen.push(api.news(
        { size: INITIAL_PER_SOURCE, sort: 'published_at', dir: 'desc' }, signal)
        .then((seite) => sammle(
          seite.items.map((news) => eintrag('news', news.id, { news })))))
    }

    if (topics.includes('research')) {
      quellen.push(api.researchCases(
        { size: INITIAL_PER_SOURCE, sort: 'last_material_update_at', dir: 'desc' },
        signal)
        .then((seite) => sammle(
          seite.items.map((fall) => eintrag('research', fall.id, { case: fall })))))
    }

    if (topics.includes('event')) {
      // Die Termine von heute — der Endpunkt nimmt den Tag als Fenster,
      // von = bis = heute trifft alles, was heute stattfindet.
      const heute = lokalerTag(new Date())
      quellen.push(api.marketEvents(
        { von: heute, bis: heute, size: 50, sort: 'date_from', dir: 'asc' }, signal)
        .then((seite) => sammle(
          seite.items.filter(terminHeute)
            .map((event) => eintrag('event', event.id, { event })))))
    }

    if (topics.includes('assessment')) {
      quellen.push(api.assessments(
        { since: heuteBeginn().toISOString(), until: heuteEnde().toISOString(),
          size: INITIAL_PER_SOURCE, sort: 'assessed_at', dir: 'desc' },
        signal)
        .then((seite) => sammle(
          seite.items.map((a) => eintrag('assessment', a.id, { assessment: a })))))
    }

    if (topics.includes('trade_idea')) {
      // Erst die Liste — wer sich heute bewegt hat. Das Detail mit dem
      // Verlauf nur für die, die heute FORTGESCHRIEBEN, aber früher angelegt
      // wurden: Nur dort sagt die Zeile etwas anderes als „Neue Idee". Und
      // NACHEINANDER, nicht auf einmal: Zwanzig Abrufe in einem Schlag
      // liefen in die Ratenbegrenzung, und mit ihnen fiel der ganze Feed.
      quellen.push(api.tradeIdeas(
        { changed_since: heuteBeginn().toISOString(),
          size: INITIAL_PER_SOURCE, sort: 'updated', dir: 'desc' },
        signal)
        .then(async (seite) => {
          const liste: (FeedEntry | null)[] = []
          for (const i of seite.items) {
            let idea = ohneVerlauf(i)
            if (istHeute(i.updated) && !istHeute(i.created)) {
              try { idea = await api.tradeIdea(i.id, {}, signal) }
              catch { /* dann eben ohne Verlauf — die Zeile steht trotzdem */ }
            }
            liste.push(eintrag('trade_idea', i.id, { idea }))
          }
          sammle(liste)
        }))
    }

    // Geladen ist, wenn jede Quelle geantwortet hat — so oder so.
    Promise.allSettled(quellen).then(() => {
      if (!signal.aborted) setLoading(false)
    })

    // ------------------------------------------------------ der Zulauf
    const ergaenzen = (eintrag: string, teil: Partial<FeedEntry>) =>
      setEntries((bisher) => zusammenfuehren(
        bisher.map((e) => e.key === eintrag ? { ...e, ...teil } : e), []))

    // Ein Hinweis, der beim Nachladen nicht zum Feed gehört — ein Termin im
    // Oktober, eine Einschätzung mit altem Stand —, wird still verworfen.
    const verwerfen = (eintrag: string) =>
      setEntries((bisher) => bisher.filter((e) => e.key !== eintrag))

    const aufnehmen = (topic: string) => (event: { id: number | null }) => {
      const neu: FeedEntry = {
        key: schluessel(topic),
        // Vorläufig die Uhr — sobald der Datensatz da ist, gilt sein
        // Erscheinungsdatum. Ohne diesen Zwischenwert hätte die Zeile keinen
        // Platz in der Sortierung, solange sie lädt.
        at: new Date(),
        topic,
        id: event.id,
        fresh: true,
      }
      setEntries((bisher) => zusammenfuehren(bisher, [neu]))

      if (event.id === null) {
        // Ohne Kennung lässt sich nichts nachladen. Die Zeile bleibt
        // trotzdem: Sie stillschweigend zu verschlucken wäre ein Verlust,
        // den niemand bemerkt.
        ergaenzen(neu.key,
                  { note: 'ohne Kennung — Beleg, Termin oder Aufgabe' })
        return
      }

      const holen: Promise<Partial<FeedEntry> | null> = (() => {
        switch (topic) {
          case 'news':
            return api.newsItem(event.id, signal).then((news) => ({ news }))
          case 'research':
            // Ohne Zielwährung: Der Feed zeigt Schlagzeilen, keine Beträge.
            return api.researchCase(event.id, {}, signal)
              .then((fall) => ({ case: fall }))
          case 'event':
            return api.marketEvent(event.id, signal)
              .then((e) => terminHeute(e) ? { event: e } : null)
          case 'assessment':
            return api.assessment(event.id, signal)
              .then((a) => istHeute(a.assessed_at) ? { assessment: a } : null)
          case 'trade_idea':
            return api.tradeIdea(event.id, {}, signal).then((idea) => ({ idea }))
          default:
            return Promise.resolve(null)
        }
      })()

      holen
        .then((teil) => {
          if (teil === null) { verwerfen(neu.key); return }
          ergaenzen(neu.key, { ...teil, at: zeitpunkt(teil) ?? new Date() })
        })
        .catch(() => {
          if (signal.aborted) return
          // Der Datensatz ist weg oder nicht lesbar. Auch das gehört in den
          // Feed — ein Hinweis ohne Inhalt sagt mehr als gar keiner.
          ergaenzen(neu.key, { note: 'nicht abrufbar' })
        })
    }

    const abmelden = topics.map((topic) => subscribe(topic, aufnehmen(topic)))
    return () => {
      controller.abort()
      for (const ab of abmelden) ab()
    }
    // `key` statt `topics`: Der Inhalt entscheidet, nicht die Identität des
    // Arrays — sonst liefe der Effekt bei jedem Rendern neu.
  }, [key, enabled])   // eslint-disable-line react-hooks/exhaustive-deps

  return { entries, loading }
}

/** Die Einträge nach Tag, in der Reihenfolge des Feeds. */
export function nachTag(entries: FeedEntry[]): [string, FeedEntry[]][] {
  const gruppen = new Map<string, FeedEntry[]>()
  for (const eintrag of entries) {
    // Der lokale Tag, nicht der UTC-Tag: Eine Meldung von 23:30 gehört zum
    // heutigen Abend und nicht zum morgigen Vormittag.
    const tag = new Date(eintrag.at.getFullYear(), eintrag.at.getMonth(),
                         eintrag.at.getDate()).toISOString()
    const vorhanden = gruppen.get(tag)
    if (vorhanden) vorhanden.push(eintrag)
    else gruppen.set(tag, [eintrag])
  }
  return [...gruppen.entries()]
}
